import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { Button } from './ui/button';
import { MessagesSquare, Target, Check, RotateCcw } from 'lucide-react';

export const ExchangeTopics = ({ youthId = null, testid = 'exchange-topics' }) => {
  const { t } = useI18n();
  const [topics, setTopics] = useState(null);
  const [challenges, setChallenges] = useState([]);

  const load = () => {
    const q = youthId ? `?youth_id=${youthId}` : '';
    api.get(`/exchange/topics${q}`).then((r) => setTopics(r.data.topics)).catch(() => setTopics([]));
    if (youthId) {
      api.get(`/exchange/challenges?youth_id=${youthId}`)
        .then((r) => setChallenges(r.data.challenges)).catch(() => {});
    }
  };
  useEffect(load, [youthId]);

  const launch = async (topicId) => {
    try {
      await api.post('/exchange/challenges', { youth_id: youthId, topic_id: topicId });
      toast.success(t('exchange.launched'));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const toggle = async (cid) => {
    try {
      await api.post(`/exchange/challenges/${cid}/toggle`);
      load();
    } catch { toast.error(t('common.error')); }
  };

  if (!topics) return null;
  const general = topics.filter((tp) => tp.chapter_number == null);
  const perChapter = topics.filter((tp) => tp.chapter_number != null);

  const renderTopic = (tp) => (
    <div key={tp.topic_id} data-testid={`exchange-topic-${tp.topic_id}`}
      className="bg-nuit-900 border border-nuit-700 rounded-2xl p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="font-bold text-creme text-sm flex items-center gap-2">
          <MessagesSquare className="w-4 h-4 text-or-400 shrink-0" strokeWidth={2.5} />
          {tp.chapter_number ? `${t('exchange.chapterN', { n: tp.chapter_number })} — ${tp.title}` : tp.title}
        </p>
        {youthId && (
          <Button size="sm" onClick={() => launch(tp.topic_id)} data-testid={`exchange-launch-${tp.topic_id}`}
            className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold h-8 px-3 text-xs gap-1">
            <Target className="w-3.5 h-3.5" strokeWidth={2.5} /> {t('exchange.launch')}
          </Button>
        )}
      </div>
      <p className="text-sm text-brume mt-2">{tp.prompt}</p>
      <p className="text-sm text-or-300/90 mt-2 font-semibold">
        {t('exchange.challengeLabel')} : {tp.challenge}
      </p>
    </div>
  );

  return (
    <div data-testid={testid}>
      {youthId && challenges.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-bold text-creme mb-2">{t('exchange.active')}</p>
          <div className="space-y-2" data-testid={`${testid}-challenges`}>
            {challenges.map((c) => (
              <div key={c.challenge_id} data-testid={`challenge-${c.challenge_id}`}
                className="flex items-center gap-3 flex-wrap bg-nuit-900/60 border border-nuit-700 rounded-2xl px-4 py-3">
                <span className={`text-xs font-bold px-3 py-1 rounded-full shrink-0 ${c.status === 'done' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-or-400/15 text-or-300'}`}
                  data-testid={`challenge-status-${c.challenge_id}`}>
                  {c.status === 'done' ? t('exchange.doneBadge') : t('exchange.launchedBadge')}
                </span>
                <p className="text-sm text-creme font-semibold flex-1 min-w-[180px]">{c.title} — {c.text}</p>
                <Button size="sm" variant="ghost" onClick={() => toggle(c.challenge_id)}
                  data-testid={`challenge-toggle-${c.challenge_id}`}
                  className={`rounded-full font-bold h-8 px-3 text-xs gap-1 ${c.status === 'done' ? 'text-brume hover:bg-nuit-700' : 'text-emerald-300 hover:bg-emerald-950'}`}>
                  {c.status === 'done'
                    ? <><RotateCcw className="w-3.5 h-3.5" /> {t('exchange.reopen')}</>
                    : <><Check className="w-3.5 h-3.5" /> {t('exchange.markDone')}</>}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {general.length > 0 && (
        <div className="space-y-3">
          {youthId && perChapter.length > 0 && (
            <p className="text-xs font-bold text-brume uppercase tracking-wider">{t('exchange.general')}</p>
          )}
          {general.map(renderTopic)}
        </div>
      )}
      {perChapter.length > 0 && (
        <div className="space-y-3 mt-4">
          <p className="text-xs font-bold text-brume uppercase tracking-wider">{t('exchange.byChapter')}</p>
          {perChapter.map(renderTopic)}
        </div>
      )}
      {topics.length === 0 && <p className="text-sm text-brume">{t('exchange.empty')}</p>}
    </div>
  );
};
